<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import type { PrStore } from '../state/pr.svelte';
  import { prPath, type PrTab } from '../router';
  import { carryFilter } from '../logic/filefilter';
  import { count } from '../logic/browse';
  import { session } from '../state/session.svelte';
  import { prefs } from '../state/prefs.svelte';
  import { applyTheme, nextTheme, themeTitle } from '../theme';
  import { nav } from '../nav.svelte';
  import { feedbackUi } from '../feedback/feedback.svelte';
  import EarlyAccess from './EarlyAccess.svelte';
  import Popover from './Popover.svelte';
  import { flushSync } from 'svelte';

  interface Props {
    pr?: PrStore;
    tab?: PrTab;
    /** The whole pull request's review progress (base → head), whatever comparison is shown. */
    progress?: { done: number; total: number; filtered?: boolean } | null;
    /** Some of the pull request's files are still being counted: the figure is a count so far. */
    counting?: boolean;
  }
  let { pr, tab, progress = null, counting = false }: Props = $props();

  function cycleTheme() {
    const next = nextTheme(prefs.value.theme);
    prefs.set('theme', next);
    applyTheme(next);
  }

  const filesCount = $derived(pr?.pr?.changedFiles ?? pr?.files?.length ?? null);
  const commitsCount = $derived(pr ? pr.snapshots.length : null);
  const ghUrl = $derived(pr?.pr?.url ?? null);
  /** The owner's policy page (an organization's, or a personal account's fixed one), read through this repository. */
  const policy = $derived(
    pr?.repository
      ? { owner: pr.repository.owner.login, href: `/settings/orgs/${encodeURIComponent(pr.repository.owner.login)}?repo=${encodeURIComponent(pr.repo)}` }
      : null,
  );
  /** The pull request's tabs, in order. */
  const tabs = $derived.by(() => {
    if (!pr) return [];
    const href = (t: PrTab) => carryFilter(prPath(pr.owner, pr.repo, pr.number, t), nav.query);
    return [
      { tab: 'overview' as PrTab, href: href('overview'), label: 'Overview', count: null, title: undefined },
      { tab: 'files' as PrTab, href: href('files'), label: 'Files', count: filesCount, title: undefined },
      { tab: 'commits' as PrTab, href: href('commits'), label: 'Snapshots', count: commitsCount || null, title: undefined },
      // One place for the viewer's unsent drafts, sending them, and what was said in review.
      {
        tab: 'review' as PrTab,
        href: href('review'),
        label: 'Review',
        count: pr.draftCount ? `(${pr.draftCount} unsent)` : null,
        title: pr.draftCount ? `${pr.draftCount} unsent, ${pr.includedCount} of them ticked to send` : 'Your unsent drafts, sending your review, and the review conversation',
      },
    ];
  });

  /** How many tabs, from the end, do not fit in the bar and are in the "More" menu instead (never clipped). */
  let overflow = $state(0);
  let navEl: HTMLElement | null = $state(null);
  let railEl: HTMLElement | null = $state(null);
  let moreEl: HTMLButtonElement | null = $state(null);
  let moreOpen = $state(false);
  const shown = $derived(tabs.slice(0, tabs.length - overflow));
  const folded = $derived(tabs.slice(tabs.length - overflow));
  const foldedCurrent = $derived(folded.find((t) => t.tab === tab) ?? null);

  /** Moves tabs into the menu, last first, until the rest fit. */
  function fit() {
    if (!navEl) return;
    overflow = 0;
    flushSync();
    while (navEl.scrollWidth > navEl.clientWidth + 1 && overflow < tabs.length) {
      overflow++;
      flushSync();
    }
    navEl.scrollLeft = 0;
  }
  $effect(() => {
    // Again when a tab's words change (its counts) or the bar's width does.
    // (flushSync cannot run inside an effect: it fits on the next frame, and the observer first fits before the bar is painted.)
    void tabs.map((t) => `${t.label}${t.count ?? ''}`).join();
    const frame = requestAnimationFrame(fit);
    if (!railEl || !navEl) return () => cancelAnimationFrame(frame);
    // The bar's width, and what is beside the tabs (the progress, the repository fill in later): never the tabs themselves,
    // which fitting resizes (that would be a ResizeObserver loop).
    const ro = new ResizeObserver(() => fit());
    ro.observe(railEl);
    for (const el of railEl.children) if (el !== navEl) ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  });

  function closeMore(refocus: boolean) {
    moreOpen = false;
    if (refocus) moreEl?.focus();
  }

  let signingOut = $state(false);
  async function signOut() {
    signingOut = true;
    // On success the page navigates away; otherwise the user is still signed in.
    if (!(await session.signOut())) signingOut = false;
  }
</script>

<header class="rail topbar" bind:this={railEl}>
  <a class="brand" href="/">Changeling<span>&nbsp;/ review</span></a>
  <EarlyAccess bar />
  {#if pr}
    <a class="pr-id" href={carryFilter(prPath(pr.owner, pr.repo, pr.number), nav.query)} title="{pr.owner}/{pr.repo}#{pr.number}"
      ><span class="mono pr-repo">{pr.owner}/{pr.repo}</span><span class="pr-num">#{pr.number}</span></a
    >
    <nav aria-label="Pull request" bind:this={navEl}>
      {#each shown as t (t.tab)}
        <a class="navlink" href={t.href} aria-current={tab === t.tab ? 'page' : undefined} title={t.title}
          >{t.label}{#if t.count !== null}<em class="count">{t.count}</em>{/if}</a
        >
      {/each}
      {#if folded.length}
        <!-- The tabs that do not fit; when the current one is among them, the button is named for it. -->
        <button
          bind:this={moreEl}
          type="button"
          class="navlink nav-more"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          aria-current={foldedCurrent ? 'page' : undefined}
          title={folded.map((t) => t.label).join(', ')}
          onclick={() => (moreOpen = !moreOpen)}>{foldedCurrent?.label ?? 'More'} <span aria-hidden="true">▾</span></button
        >
      {/if}
    </nav>
    {#if moreOpen && moreEl}
      <Popover anchor={moreEl} role="menu" label="More tabs" onclose={closeMore}>
        {#each folded as t (t.tab)}
          <a role="menuitem" tabindex="-1" class="nav-more-item" href={t.href} aria-current={tab === t.tab ? 'page' : undefined} title={t.title} onclick={() => closeMore(false)}
            >{t.label}{#if t.count !== null}<em>{t.count}</em>{/if}</a
          >
        {/each}
      </Popover>
    {/if}
  {:else}
    <nav aria-label="Main">
      <a class="navlink" href="/" aria-current={nav.route.name === 'dashboard' ? 'page' : undefined}>Dashboard</a>
      {#if session.user?.operator}
        <a class="navlink" href="/feedback" aria-current={nav.route.name === 'feedback' || nav.route.name === 'feedback-report' ? 'page' : undefined}
          >Feedback reports</a
        >
      {/if}
    </nav>
  {/if}
  <div class="bottom">
    {#if progress && progress.total}
      {@const said = `${count(progress.done)} of ${count(progress.total)} lines reviewed in the whole pull request (base → head)${progress.filtered ? ', in the files the filter keeps' : ''}${counting ? ' · still counting' : ''}`}
      <span class="bar-progress" title={said}>
        <span class="progress" aria-hidden="true"><i style="width:{(progress.done / progress.total) * 100}%"></i></span>
        <small aria-label={said}>{count(progress.done)}/{count(progress.total)}{progress.filtered ? ' · filtered' : ''}</small>
      </span>
    {/if}
    {#if ghUrl}<a href={ghUrl} target="_blank" rel="noreferrer">GitHub ↗</a>{/if}
    {#if policy}<a href={policy.href} title="{policy.owner} review policy">Policy</a>{/if}
    {#if session.user}
      <button class="theme-btn feedback-btn" onclick={() => feedbackUi.show()} title="Send feedback about Changeling{keyboard.hint({ kind: 'open', menu: 'feedback' })}"
        >Feedback</button
      >
    {/if}
    <button class="theme-btn" onclick={cycleTheme} aria-label={themeTitle(prefs.value.theme)} title={themeTitle(prefs.value.theme)}>◐ {prefs.value.theme}</button>
    {#if session.user}
      <a
        class="theme-btn settings-link"
        href="/settings"
        aria-label="Settings"
        aria-current={nav.route.name === 'settings' ? 'page' : undefined}
        title="Settings{keyboard.hint({ kind: 'open', menu: 'settings' })}"
        ><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M6.43 2.94L6.82 1.10L9.18 1.10L9.57 2.94L10.47 3.31L12.04 2.28L13.72 3.96L12.69 5.53L13.06 6.43L14.90 6.82L14.90 9.18L13.06 9.57L12.69 10.47L13.72 12.04L12.04 13.72L10.47 12.69L9.57 13.06L9.18 14.90L6.82 14.90L6.43 13.06L5.53 12.69L3.96 13.72L2.28 12.04L3.31 10.47L2.94 9.57L1.10 9.18L1.10 6.82L2.94 6.43L3.31 5.53L2.28 3.96L3.96 2.28L5.53 3.31ZM8 5.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8Z" /></svg></a
      >
      <div class="me">
        {#if session.user.avatar_url}<img class="avatar" src={session.user.avatar_url} alt="" />{/if}
        <span class="login" title="Signed in as {session.user.login}">{session.user.login}</span>
        <button class="signout" onclick={signOut} disabled={signingOut}>{signingOut ? 'Signing out…' : 'Sign out'}</button>
      </div>
    {/if}
  </div>
  {#if session.signOutError}<p class="signout-error" role="alert">{session.signOutError}</p>{/if}
  <!-- While a write or its reload is in flight (PrStore.saving). -->
  {#if pr?.saving}<div class="progress-line" role="progressbar" aria-label="Saving changes"></div>{/if}
</header>
